import React, { useState, useEffect, useRef } from 'react';
import { Search, FolderKanban, CheckSquare, Users, MessageSquare, FileText, X, ArrowRight, Download } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { useUserProfileModal } from '../../context/UserProfileModalContext.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { StatusBadge, PriorityBadge } from '../common/Badge.tsx';

interface SearchResults {
  projects: any[];
  tasks: any[];
  members: any[];
  messages: any[];
  files: any[];
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProject: (projectId: string) => void;
  onSelectTask: (taskId: string, projectId: string) => void;
  onSelectChat: (channelId: string) => void;
}

export const GlobalCommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onSelectProject,
  onSelectTask,
  onSelectChat,
}) => {
  const { token } = useAuth();
  const { openUserProfile, navigateToChatWithUser } = useUserProfileModal();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>({
    projects: [],
    tasks: [],
    members: [],
    messages: [],
    files: [],
  });
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEscapeKey(isOpen, onClose);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResults({ projects: [], tasks: [], members: [], messages: [], files: [] });
    }
  }, [isOpen]);

  // Debounced search query
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed || !token) {
      setResults({ projects: [], tasks: [], members: [], messages: [], files: [] });
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(trimmed)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setResults(data.results || { projects: [], tasks: [], members: [], messages: [], files: [] });
        }
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setIsSearching(false);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [query, token]);

  if (!isOpen) return null;

  const totalResultsCount =
    results.projects.length +
    results.tasks.length +
    results.members.length +
    results.messages.length +
    results.files.length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-4 sm:pt-24 px-2.5 sm:px-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-surface-950/60 backdrop-blur-sm" onClick={onClose} />

      {/* Palette Modal */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-surface-900 rounded-3xl shadow-modal border border-surface-200 dark:border-surface-800 overflow-hidden z-10 animate-scale-up">
        {/* Search Input Bar */}
        <div className="flex items-center px-3.5 sm:px-4 py-3 border-b border-surface-200 dark:border-surface-800">
          <Search className="w-5 h-5 text-surface-400 mr-2.5 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects, tasks, members, files, messages..."
            className="w-full bg-transparent border-none text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-0 text-base min-h-[40px]"
          />
          {query ? (
            <button
              onClick={() => setQuery('')}
              className="p-1.5 text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 rounded-lg shrink-0"
              aria-label="Clear query"
            >
              <X className="w-4 h-4" />
            </button>
          ) : null}
          <button
            onClick={onClose}
            className="sm:hidden p-1.5 ml-1 text-surface-500 hover:text-surface-900 dark:hover:text-surface-100 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg shrink-0"
            aria-label="Close search"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close (Esc)"
            className="hidden sm:inline-block ml-2 px-2 py-0.5 text-xs text-surface-400 dark:text-surface-500 bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 dark:hover:bg-surface-700 rounded font-mono border border-surface-200 dark:border-surface-700 cursor-pointer"
          >
            ESC
          </button>
        </div>

        {/* Results List */}
        <div className="max-h-[75vh] sm:max-h-[60vh] overflow-y-auto p-3 space-y-4 touch-scroll">
          {isSearching && (
            <div className="py-8 text-center text-sm text-surface-500 flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-brand-500/20 border-t-brand-500 rounded-full animate-spin" />
              <span>Searching workspace records...</span>
            </div>
          )}

          {!isSearching && query.trim() && totalResultsCount === 0 && (
            <div className="py-8 text-center text-sm text-surface-500">
              No matching results found for "{query}".
            </div>
          )}

          {!query.trim() && (
            <div className="py-6 px-4 text-center text-xs text-surface-400 dark:text-surface-500">
              Type to instantly search across company projects, tasks, employees, chat, and files.
            </div>
          )}

          {/* Projects Results */}
          {results.projects.length > 0 && (
            <div>
              <div className="flex items-center px-2 py-1 text-xs font-semibold uppercase tracking-wider text-surface-400 dark:text-surface-500">
                <FolderKanban className="w-3.5 h-3.5 mr-1.5" />
                Projects ({results.projects.length})
              </div>
              <div className="mt-1 space-y-1">
                {results.projects.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      onSelectProject(p.id);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-left rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors group"
                  >
                    <div className="flex items-center space-y-0.5">
                      <span
                        className="w-3 h-3 rounded-full mr-2.5 shrink-0"
                        style={{ backgroundColor: p.color || '#026fc1' }}
                      />
                      <div>
                        <div className="font-medium text-sm text-surface-900 dark:text-surface-100 flex items-center">
                          <span className="font-mono text-xs text-brand-600 dark:text-brand-400 mr-2">[{p.code || p.key}]</span>
                          {p.name}
                        </div>
                        {p.description && (
                          <div className="text-xs text-surface-500 line-clamp-1">{p.description}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <StatusBadge status={p.status} size="sm" />
                      <ArrowRight className="w-4 h-4 text-surface-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tasks Results */}
          {results.tasks.length > 0 && (
            <div>
              <div className="flex items-center px-2 py-1 text-xs font-semibold uppercase tracking-wider text-surface-400 dark:text-surface-500">
                <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                Tasks ({results.tasks.length})
              </div>
              <div className="mt-1 space-y-1">
                {results.tasks.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => {
                      onSelectTask(t.id, t.projectId);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-left rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors group"
                  >
                    <div className="space-y-0.5 max-w-[70%]">
                      <div className="font-medium text-sm text-surface-900 dark:text-surface-100 line-clamp-1 flex items-center">
                        {t.code && (
                          <span className="font-mono text-xs font-bold text-brand-600 dark:text-brand-400 mr-2 shrink-0">
                            [{t.code}]
                          </span>
                        )}
                        <span>{t.title}</span>
                      </div>
                      <div className="text-xs text-surface-500">
                        {t.projectName && (
                          <span>Project: <span className="font-medium text-surface-700 dark:text-surface-300 mr-2">{t.projectName}</span></span>
                        )}
                        {t.assigneeName && (
                          <span>Assignee: <span className="font-medium text-surface-700 dark:text-surface-300">{t.assigneeName}</span></span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {t.priority && <PriorityBadge priority={t.priority} size="sm" />}
                      {t.status && <StatusBadge status={t.status} size="sm" />}
                      <ArrowRight className="w-4 h-4 text-surface-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Members Results */}
          {results.members.length > 0 && (
            <div>
              <div className="flex items-center px-2 py-1 text-xs font-semibold uppercase tracking-wider text-surface-400 dark:text-surface-500">
                <Users className="w-3.5 h-3.5 mr-1.5" />
                Team Members ({results.members.length})
              </div>
              <div className="mt-1 space-y-1">
                {results.members.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => {
                      openUserProfile(m);
                      onClose();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        openUserProfile(m);
                        onClose();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors group text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                  >
                    <div className="flex items-center space-x-2.5 min-w-0">
                      <Avatar src={m.avatarUrl} name={m.fullName} size="sm" />
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-surface-900 dark:text-surface-100 truncate">
                          {m.fullName}
                        </div>
                        <div className="text-xs text-surface-500 truncate">
                          {m.title || m.departmentName || m.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded bg-surface-200 dark:bg-surface-800 text-surface-600 dark:text-surface-400 font-medium capitalize">
                        {m.role}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToChatWithUser(m);
                          onClose();
                        }}
                        title={`Send direct message to ${m.fullName}`}
                        className="p-1.5 rounded-lg text-surface-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/50 transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      <ArrowRight className="w-4 h-4 text-surface-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Messages Results */}
          {results.messages.length > 0 && (
            <div>
              <div className="flex items-center px-2 py-1 text-xs font-semibold uppercase tracking-wider text-surface-400 dark:text-surface-500">
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                Messages ({results.messages.length})
              </div>
              <div className="mt-1 space-y-1">
                {results.messages.map((msg) => (
                  <button
                    key={msg.id}
                    onClick={() => {
                      onSelectChat(msg.channelId);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-left rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors group cursor-pointer"
                  >
                    <div className="max-w-[80%]">
                      <div className="text-xs text-surface-500 flex items-center space-x-1.5">
                        <span className="font-semibold text-surface-700 dark:text-surface-300">{msg.senderName}</span>
                        <span>in #{msg.channelName}</span>
                      </div>
                      <div className="text-sm text-surface-900 dark:text-surface-100 line-clamp-1 mt-0.5">
                        {msg.content}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-surface-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Files Results */}
          {results.files.length > 0 && (
            <div>
              <div className="flex items-center px-2 py-1 text-xs font-semibold uppercase tracking-wider text-surface-400 dark:text-surface-500">
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Files ({results.files.length})
              </div>
              <div className="mt-1 space-y-1">
                {results.files.map((f) => (
                  <div
                    key={f.id}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors group"
                  >
                    <a
                      href={`/api/files/${f.id}/download?token=${encodeURIComponent(token || '')}`}
                      download={f.originalName}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => onClose()}
                      className="flex items-center space-x-2.5 min-w-0 flex-1 cursor-pointer"
                      title={`Download ${f.originalName}`}
                    >
                      <FileText className="w-4 h-4 text-brand-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium text-surface-900 dark:text-surface-100 truncate block hover:underline">
                          {f.originalName}
                        </span>
                        <div className="text-xs text-surface-500 flex items-center space-x-2">
                          {f.projectName && (
                            <span>Project: <strong className="font-medium text-surface-700 dark:text-surface-300">{f.projectName}</strong></span>
                          )}
                          {f.fileSize && <span>({(f.fileSize / 1024).toFixed(1)} KB)</span>}
                        </div>
                      </div>
                    </a>
                    <div className="flex items-center space-x-1.5 shrink-0 ml-2">
                      {f.taskId && (
                        <button
                          type="button"
                          onClick={() => {
                            onSelectTask(f.taskId, f.projectId);
                            onClose();
                          }}
                          title="Open associated task"
                          className="text-xs px-2 py-1 rounded-lg bg-brand-50 hover:bg-brand-100 dark:bg-brand-950/40 dark:hover:bg-brand-900/50 text-brand-700 dark:text-brand-300 transition-colors cursor-pointer font-semibold"
                        >
                          Task
                        </button>
                      )}
                      {f.projectId && (
                        <button
                          type="button"
                          onClick={() => {
                            onSelectProject(f.projectId);
                            onClose();
                          }}
                          title={`Open project ${f.projectName || ''}`}
                          className="text-xs px-2 py-1 rounded-lg bg-surface-200/80 dark:bg-surface-800 text-surface-700 dark:text-surface-300 hover:bg-brand-100 hover:text-brand-700 dark:hover:bg-brand-900/40 transition-colors cursor-pointer"
                        >
                          Project
                        </button>
                      )}
                      <a
                        href={`/api/files/${f.id}/download?token=${encodeURIComponent(token || '')}`}
                        download={f.originalName}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => onClose()}
                        title="Download file"
                        className="p-1.5 rounded-lg text-surface-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/50 transition-colors cursor-pointer"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
